import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { CallService } from '../../phone/_services/call.service';
import { Call, CallTimelineEvent } from '../../phone/_types/call';
import { Router } from '@angular/router';
import { RecordingService } from '../../phone/_services/recording.service';
import { User } from '../../users/_types/user';
import { UsersService } from '../../users/_services/users.service';
import { FilterFieldConfig } from 'src/app/core/filter-panel/filter-field.model';

@Component({
    selector: 'app-cdr.component',
    templateUrl: './cdr.component.html',
    styleUrls: ['./cdr.component.css'],
    host: { class: 'list-page' },
    standalone: false
})
export class VOIPCDRComponent implements OnInit {
  public calls: Call[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  private lastGridEvent: any;
  public filters: any = {};

  public statuses: any[] = [
    { label: 'Todos', value: '', severity: '' },
  ];

  public callTypes: any[] = [
    { label: 'Todas', value: '', severity: '' },
  ];

  // opciones del filtro de tipo: sin "Interna", que ya cubre el filtro de dirección
  public callTypeOptions: any[] = [];

  public directions: any[] = [
    { label: 'Todas', value: '' },
  ];

  // usuarios con extensión SIP para los combos "Usuario origen" / "Usuario destino"
  public users: User[] = [];
  public userOptions: any[] = [];

  public userFilterOptions: { label: string; value: any }[] = [];
  public fieldsConfig: FilterFieldConfig[] = [];

  // diálogo de detalle de la llamada
  public detailVisible: boolean = false;
  public selectedCall: Call | null = null;
  public timeline: CallTimelineEvent[] = [];
  public loadingTimeline: boolean = false;
  public playDetailRecording: boolean = false;

  constructor(
    public topbarService: TopbarService,
    public callService: CallService,
    private router: Router,
    private recordingService: RecordingService,
    private usersService: UsersService,
  ) {
    this.resetFilters();
  }

  ngOnInit() {
    this.topbarService.setTitle("Registro de llamadas");
    this.statuses = this.statuses.concat(this.callService.getStatuses());
    this.callTypes = this.callTypes.concat(this.callService.getCallTypes());
    this.callTypeOptions = this.callTypes.filter((item: any) => item.value !== 'internal');
    this.directions = this.directions.concat(this.callService.getDirections());
    this.fieldsConfig = [
      { filter: this.filters.startFrom, filterTo: this.filters.startTo, type: 'date', label: 'Fecha', base: true },
      { filter: this.filters.status, type: 'select', label: 'Estado', options: this.toFilterOptions(this.statuses), base: true },
      { filter: this.filters.direction, type: 'select', label: 'Dirección', options: this.toFilterOptions(this.directions), base: true },
      { filter: this.filters.callType, type: 'select', label: 'Tipo de llamada', options: this.toFilterOptions(this.callTypeOptions), base: true },
      { filter: this.filters.srcNumber, type: 'text', label: 'Tel. origen' },
      { filter: this.filters.dstNumber, type: 'text', label: 'Tel. destino' },
      { filter: this.filters.originUser, type: 'select', label: 'Usuario origen', options: this.userFilterOptions, filterable: true },
      { filter: this.filters.answeredByUser, type: 'select', label: 'Usuario destino', options: this.userFilterOptions, filterable: true },
      { filter: this.filters.queueName, type: 'text', label: 'Cola' },
      { filter: this.filters.linkedId, type: 'text', label: 'Call ID' }
    ];
    this.loadUsers();
  }

  private toFilterOptions(items: any[]): { label: string; value: any }[] {
    return items.filter((item: any) => item.value !== '').map((item: any) => ({ label: item.label, value: item.value }));
  }

  async loadUsers() {
    const response = await firstValueFrom(this.usersService.all({ sort: ['name'], page: { number: 1, size: 1000 } }));
    this.users = response.data.filter((user: User) => user.enableSIPExtension && user.sipExtension);
    this.userOptions = this.users.map((user: User) => ({
      name: `${user.name} <${user.sipExtension}>`,
      value: user.id,
    }));
    this.userFilterOptions.length = 0;
    this.userFilterOptions.push(...this.userOptions.map((option: any) => ({ label: option.name, value: option.value })));
  }

  async loadData($event: any) {
    this.lastGridEvent = $event;

    let params: IParamsCollection = {
      page: { number: $event.first + 1, size: $event.rows },
      sort: ['-start']
    };

    if (typeof $event.sortField != "undefined") params.sort = [($event.sortOrder > 0 ? "-" : "") + $event.sortField];

    params.filter = FilterBuilder.fromObject(this.filters).build();

    let response = await firstValueFrom(this.callService.all(params));
    this.calls = response.data;
    this.totalRecords = response.meta['total'];
    this.topbarService.setCount(this.totalRecords);
  }

  resetFilters() {
    this.filters = {
      startFrom: new Filter(FilterOperation.greaterOrEqual, 'start'),
      startTo: new Filter(FilterOperation.lessOrEqual, 'start'),
      srcNumber: new Filter(FilterOperation.contains, 'srcNumber'),
      dstNumber: new Filter(FilterOperation.contains, 'dstNumber'),
      originUser: new Filter(FilterOperation.equals, 'originUserId'),
      answeredByUser: new Filter(FilterOperation.equals, 'answeredByUserId'),
      status: new Filter(FilterOperation.equals, 'status'),
      direction: new Filter(FilterOperation.equals, 'direction'),
      callType: new Filter(FilterOperation.equals, 'callType'),
      queueName: new Filter(FilterOperation.contains, 'queueName'),
      caseNumber: new Filter(FilterOperation.contains, 'caseNumber'),
      linkedId: new Filter(FilterOperation.equals, 'linkedId'),
    };
  }

  search() {
    this.lastGridEvent.first = 0;
    this.loadData(this.lastGridEvent);
  }

  hasRecording(call: Call): boolean {
    return !!call.recordingRef;
  }

  getRecordingUrl(call: Call): string {
    return this.recordingService.getDownloadUrlByUniqueId(call.recordingRef!);
  }

  goToCase(caseId: string) {
    this.router.navigate(['/cases', caseId]);
  }

  getStatusLabel(call: Call): string {
    return this.callService.getStatusLabel(call.status);
  }

  getStatusSeverity(call: Call): any {
    return this.callService.getStatusSeverity(call.status);
  }

  getDirectionLabel(call: Call): string {
    return this.callService.getDirectionLabel(call.direction);
  }

  getDirectionIcon(call: Call): string {
    return this.callService.getDirectionIcon(call.direction);
  }

  getCallTypeLabel(call: Call): string {
    return this.callTypes.find((item: any) => item.value === call.callType?.toLowerCase())?.label ?? '-';
  }

  /** Segundos a "Xm Ys" / "Ys". */
  formatSeconds(total?: number): string {
    let t = (total && total > 0) ? total : 0;
    const m = Math.floor(t / 60);
    const s = t % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  async openDetail(call: Call) {
    this.selectedCall = call;
    this.detailVisible = true;
    this.playDetailRecording = false;
    this.timeline = [];
    this.loadingTimeline = true;
    try {
      this.timeline = await firstValueFrom(this.callService.getTimeline(call.id!));
    } finally {
      this.loadingTimeline = false;
    }
  }

  timelineIcon(ev: CallTimelineEvent): string {
    return this.callService.getTimelineKind(ev.kind).icon;
  }

  timelineColor(ev: CallTimelineEvent): string {
    return this.callService.getTimelineKind(ev.kind).color;
  }

  /** Segundos desde el inicio de la llamada (primer evento de la cronología). */
  timelineOffset(ev: CallTimelineEvent): number {
    if (this.timeline.length === 0) return 0;
    const start = new Date(this.timeline[0].at).getTime();
    return Math.max(0, Math.round((new Date(ev.at).getTime() - start) / 1000));
  }

  filterByLinkedId(linkedId: string) {
    // this.resetFilters();
    Object.values(this.filters).forEach((filter: any) => filter.value = null);
    this.filters.linkedId.value = linkedId;
    this.search();
  }
}
